/**
 * ExecutiveReport — the monthly management report for a business owner.
 *
 * The reader is a director with little time and no reason to know what
 * autotargeting is. The page is built as one argument in three movements,
 * and the anchor rail names them:
 *
 *   1. what is happening   — summary, score, money at risk, campaigns,
 *                            enquiries, team regularity, incident volume
 *   2. what to do          — questions for the team, tasks for specialists
 *   3. control             — the next checkpoint and how to not miss it
 *
 * Страница презентационная и принимает снимок `ProjectReportData` как есть.
 * Этаж рисуется только если для него пришли данные: у кабинета на другой
 * метрике медиаплана нет плана по обращениям, а при `narrativeFailed` нет
 * текстов.
 *
 * Из этого правила одно исключение: секция правок может не строиться при
 * живом отчёте (`editsStatus.status = NOT_APPLICABLE`), и на её месте
 * встаёт заглушка с причиной. Молча пропавший раздел читателю неотличим
 * от поломки страницы.
 *
 * @format
 */

import {createContext, useCallback, useContext, useMemo, useRef, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {ChipMUI} from 'ui/components/atoms/ChipMUI';
import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {AppHeader} from 'ui/components/organisms/AppHeader';
import {spacingPx} from 'ui/components/layout/spacing';
import {iconPxForRole, text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {ACTIVITY_FALLBACK_NOTE, AD_SYSTEM_LABEL, SECTION_BY_SCORE_PART} from './content';
import {FLOORS} from './floors';
import {CAMPAIGNS, PAGE_MAX_WIDTH, TASKS, longDate, pluralize, reportPeriodTitle} from './helpers';
import {exportChecklist} from './checklist';
import {useTypographDom} from './typographDom';
import {AdSystemIcon} from './components/AdSystemIcon';
import {AnchorNav, useActiveSection} from './components/AnchorNav';
import {CampaignsSection} from './components/CampaignsSection';
import {ClientsSection} from './components/ClientsSection';
import {ControlSection, NextCheckpointDate} from './components/ControlSection';
import {FloorHeading} from './components/FloorHeading';
import {FloorScore} from './components/FloorScore';
import {PublicLinkPanel} from './components/PublicLinkPanel';
import {IncidentsSection} from './components/IncidentsSection';
import {NotificationsSection} from './components/NotificationsSection';
import {QuestionsSection} from './components/QuestionsSection';
import {ReportFooter} from './components/ReportFooter';
import {RisksSection, visibleRiskRows} from './components/RisksSection';
import {ScoreSection} from './components/ScoreSection';
import {SummarySection} from './components/SummarySection';
import {TasksSection} from './components/TasksSection';
import {TeamSection} from './components/TeamSection';
import {TeamUnmeasuredSection} from './components/TeamUnmeasuredSection';
import {ZeroResultSection} from './components/ZeroResultSection';
import {
  PAGE_GAP,
  SECTION_IDS,
  type IsoDate,
  type NavAnchor,
  type NotificationsData,
  type PublicLink,
  type ReportData,
  type ScorePart,
  type SectionId,
} from './types';

export interface ExecutiveReportProps {
  /** Снимок отчёта. */
  data: ReportData;
  /** Дата съёма данных: `generatedAt` отчёта, без времени. */
  dataDate: IsoDate;
  /**
   * Подписки — текущее состояние из `company.notifications`.
   *
   * Необязательны: страница показывает документ, а подписка живёт вне
   * снимка. Нет состояния — нет и блока: тумблер, который не отражает
   * действительность, хуже отсутствующего.
   */
  notifications?: NotificationsData | null;
  /**
   * Публичная ссылка на этот отчёт и управление ею.
   *
   * Необязательно: панель показывается только тому, кто открыл отчёт в
   * системе. Гость получает страницу без этих пропов и про ссылку не знает
   * ничего — в том числе того, что она вообще бывает.
   */
  publicLink?: PublicLink | null;
  onCreatePublicLink?: () => void;
  onRevokePublicLink?: () => void;
  /** Запрос по ссылке в полёте: кнопки панели не должны срабатывать дважды. */
  publicLinkBusy?: boolean;
  /**
   * Сообщение последней неудачной попытки создать/отозвать ссылку.
   *
   * Хост сбрасывает его сам перед новой попыткой — панель не решает, когда
   * ошибка перестала быть актуальной.
   */
  publicLinkError?: string | null;
  /** Hook for the printable checklist; wired by the host app. */
  onPrintChecklist?: () => void;
  /**
   * Клик по логотипу в шапке документа.
   *
   * Документ открывают по прямой ссылке (из письма, из архива) — своей
   * навигации у страницы нет, поэтому куда ведёт логотип решает хост.
   * Нет обработчика — `AppHeader` рисует логотип без действия.
   */
  onLogoClick?: () => void;
}

/**
 * Признаки этажей, у которых условие отрисовки — составное выражение
 * (не голое поле снимка), а не только «есть объект / нет объекта».
 *
 * Рейка не имеет права заново выводить такое условие: одна и та же
 * логика, написанная дважды, рано или поздно разойдётся, и чип поведёт в
 * пустой этаж. `ReportBody` считает эти флаги один раз и передаёт готовыми
 * — и сюда, и в JSX, где стоит сам этаж.
 */
interface CompositeFloorFlags {
  hasRisks: boolean;
  hasZeroResult: boolean;
  hasTeam: boolean;
}

/**
 * Какие этажи на странице есть. Считается один раз и расходится двум
 * потребителям: рейке навигации и кнопкам «Подробнее» из резюме. Вывести
 * это условие второй раз означало бы рано или поздно разойтись с первым —
 * и повести читателя в пустоту.
 */
function sectionPresence(
  data: ReportData,
  hasNotifications: boolean,
  {hasRisks, hasZeroResult, hasTeam}: CompositeFloorFlags,
): Record<SectionId, boolean> {
  const {project} = data;
  return {
    summary: Boolean(project.summary?.length),
    // Без зон шкалы кольцу и линейке нечего рисовать — этаж не строится,
    // хотя `score` пришёл. Без второго множителя чип рейки вёл бы в
    // пустой этаж (то самое расхождение, ради которого завели словарь).
    score:
      project.score !== null && project.score !== undefined && Boolean(project.scoreZones?.length),
    risks: hasRisks,
    zeroResult: hasZeroResult,
    campaigns: Boolean(project.campaigns?.length),
    clients: Boolean(project.clients),
    team: hasTeam,
    incidents: Boolean(project.incidents),
    questions: Boolean(project.questions?.length),
    tasks: Boolean(project.tasks?.length),
    control: Boolean(project.control),
    notifications: hasNotifications,
  };
}

/**
 * Навигация — проекция этажей, а не второй их список: подпись раздела
 * живёт в одном месте и не расходится между рейкой и заголовком. Этажи без
 * данных из рейки выпадают: чип, ведущий в пустоту, хуже отсутствующего.
 */
function navAnchorsFor(present: Record<SectionId, boolean>): NavAnchor[] {
  return FLOORS.filter(floor => present[floor.id]).map(floor => ({
    id: floor.id,
    label: floor.navLabel,
    stage: floor.stage,
  }));
}

// The page canvas is `background` (#141418); cards sit on it as `surface1`.
const Page = styled(Box)(({theme}) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
  color: surfaceVar('onSurface'),
}));

const Content = styled(Stack)({
  maxWidth: PAGE_MAX_WIDTH,
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingLeft: spacingPx('l'),
  paddingRight: spacingPx('l'),
  // Граница «хром приложения / документ» крупнее, чем граница между
  // этажами, поэтому отступ берём тот же удвоенный шаг, что разводит этажи.
  paddingTop: spacingPx('xl') * 2,
  paddingBottom: spacingPx('xl'),
  gap: spacingPx('xl') * 2,
});

/**
 * Кегль иконки в бейдже реквизитов. Берётся из той же роли, по которой
 * `ChipMUI` размера `s` считает свои иконки.
 */
const ICON_PX = iconPxForRole('label.m');

/** Что это за документ. Строкой выше титула, поэтому кеглем ниже него. */
const Kicker = styled(Box)({
  ...text('heading.l'),
  color: surfaceVar('onSurfaceLight'),
});

/**
 * Титул отчёта выходит за типографическую шкалу: верх шкалы занят
 * заголовками этажей, а титул обязан стоять над ними.
 */
const ReportTitle = styled(Box)({
  ...text('heading.xl'),
  fontSize: '44px',
  lineHeight: '48px',
  color: surfaceVar('onSurface'),
  margin: 0,
});

/**
 * Колонка документа.
 *
 * `minWidth: 0` обязателен: без него флекс-элемент не ужимается ниже
 * своего содержимого, а внутри лежат таблицы и графики шириной в экран —
 * колонка распирала бы ряд и выталкивала рейку за край.
 */
const Floors = styled(Stack)({
  flex: '1 1 0',
  minWidth: 0,
  // Втрое от `xl` (32) — это 96. Разрыв блоков ВНУТРИ этажа равен `xl`, и
  // пока граница этажей была вдвое больше, она читалась как ещё один такой
  // же блок. У этажа теперь и заголовок, и объяснение, и вклад в оценку —
  // связка стала выше, и отделять её надо заметнее, иначе следующий
  // заголовок читается продолжением предыдущего раздела.
  gap: spacingPx('xl') * 3,
});

const Anchored = styled(Box)({
  scrollMarginTop: 72,
});

/** Сообщение о том, что текстовая часть отчёта не сформирована. */
const NarrativeNotice = styled(Box)(({theme}) => ({
  ...text('body.l'),
  color: theme.palette.alert.main,
}));

/**
 * Слагаемые балла, за которые отвечает этаж. У большинства — одно, у
 * инцидентов два, у светофора и вопросов — ни одного.
 */
function scorePartsFor(id: SectionId, parts: ScorePart[] | null): ScorePart[] {
  return (parts ?? []).filter(part => SECTION_BY_SCORE_PART[part.key] === id);
}

/**
 * Разбор балла — для всех этажей сразу.
 *
 * Контекст, а не проп на каждом `<Floor>`: этажей дюжина, и одинаковый
 * аргумент, который надо не забыть в двенадцатом месте, забудут в
 * тринадцатом. Значение одно на весь отчёт и за время чтения не меняется.
 */
const ScorePartsContext = createContext<ScorePart[] | null>(null);

/**
 * Этаж: якорь, заголовок с объяснением и контент. Этаж без `title`
 * (краткое резюме) рендерится голым: это первый экран, а не раздел.
 */
function Floor({
  id,
  badges,
  children,
  hideDescription,
}: {
  id: SectionId;
  badges?: ReactNode;
  children: ReactNode;
  /**
   * Гасит `description` этажа, заголовок остаётся. Нужно там, где вместо
   * контента стоит заглушка: описание обещает данные, которых заглушка не
   * даёт, а заголовок остаётся верным в любом случае — он только называет
   * раздел.
   */
  hideDescription?: boolean;
}) {
  const floor = FLOORS.find(f => f.id === id);
  const parts = scorePartsFor(id, useContext(ScorePartsContext));
  return (
    <Anchored id={id}>
      {floor?.title ? (
        <FloorHeading
          title={floor.title}
          description={hideDescription ? undefined : floor.description}
          badges={badges}
          aside={parts.length > 0 ? <FloorScore parts={parts} /> : undefined}
        >
          {children}
        </FloorHeading>
      ) : (
        children
      )}
    </Anchored>
  );
}

export function ExecutiveReport({
  data,
  dataDate,
  notifications,
  publicLink,
  onCreatePublicLink,
  onRevokePublicLink,
  publicLinkBusy,
  publicLinkError,
  onPrintChecklist,
  onLogoClick,
}: ExecutiveReportProps) {
  return (
    <ReportBody
      data={data}
      dataDate={dataDate}
      notifications={notifications}
      publicLink={publicLink}
      onCreatePublicLink={onCreatePublicLink}
      onRevokePublicLink={onRevokePublicLink}
      publicLinkBusy={publicLinkBusy}
      publicLinkError={publicLinkError}
      onPrintChecklist={onPrintChecklist}
      onLogoClick={onLogoClick}
    />
  );
}

function ReportBody({
  data,
  dataDate,
  notifications,
  publicLink,
  onCreatePublicLink,
  onRevokePublicLink,
  publicLinkBusy,
  publicLinkError,
  onPrintChecklist,
  onLogoClick,
}: {
  data: ReportData;
  dataDate: IsoDate;
  notifications?: NotificationsData | null;
  publicLink?: PublicLink | null;
  onCreatePublicLink?: () => void;
  onRevokePublicLink?: () => void;
  publicLinkBusy?: boolean;
  publicLinkError?: string | null;
  onPrintChecklist?: () => void;
  onLogoClick?: () => void;
}) {
  const {meta, project} = data;
  const risks = project.risks ?? [];
  // Единственное место, где считаются оба признака: отсюда они уходят в
  // `sectionPresence` (та строит словарь `present`, из которого рейка
  // `navAnchorsFor` берёт свои чипы) и одновременно читаются самим этажом
  // ниже напрямую — пересчитать условие второй раз означало бы разойтись
  // с первым.
  // Голого `risks.length` не хватает: секция сама пропускает строки без
  // `spend`, и набор из одной такой строки без заметки-замены дал бы
  // непустой `risks`, но пустую карточку под суммой (см. `visibleRiskRows`).
  const hasRisks = visibleRiskRows(risks).length > 0 || Boolean(project.riskNote);
  const hasZeroResult = Boolean(project.zeroResult?.campaigns?.length);
  // Секция правок гаснет целиком, когда наблюдать было нечем. Признак —
  // только статус: пустой `edits` без статуса значит «отчёт собран до
  // появления поля», и объяснять там нечего.
  const teamUnmeasured = project.editsStatus?.status === 'NOT_APPLICABLE';
  const hasTeamCalendar = Boolean(project.edits?.calendar?.length);
  const hasTeam = hasTeamCalendar || teamUnmeasured;
  const present = useMemo(
    () => sectionPresence(data, Boolean(notifications), {hasRisks, hasZeroResult, hasTeam}),
    [data, notifications, hasRisks, hasZeroResult, hasTeam],
  );
  const anchors = useMemo(() => navAnchorsFor(present), [present]);
  const sectionIds = useMemo(() => anchors.map(a => a.id), [anchors]);
  const activeId = useActiveSection(sectionIds);

  const handleNavigate = useCallback((id: SectionId) => {
    document.getElementById(id)?.scrollIntoView({behavior: 'smooth', block: 'start'});
  }, []);

  /**
   * Чек-лист файлом, а не печатью страницы.
   *
   * `window.print()` выводил на бумагу сам отчёт — тёмный фон, рейку
   * навигации, свёрнутые раскрытия и десять этажей, из которых
   * исполнителю нужен один. Вместо этого собирается отдельный документ
   * с задачами, кампаниями и местом под подпись.
   *
   * Хост по-прежнему может перехватить печать своим обработчиком: у него
   * может быть собственный экспорт.
   */
  const handlePrint = useCallback(() => {
    if (onPrintChecklist) {
      onPrintChecklist();
      return;
    }
    exportChecklist(project.tasks ?? [], {
      companyName: project.name,
      adSystem: AD_SYSTEM_LABEL[meta.adSystem] ?? meta.adSystem,
      period: reportPeriodTitle(meta.periodStart, meta.periodEnd),
      dataDate: longDate(dataDate),
      nextReportDate: project.control ? longDate(project.control.nextReportDate) : null,
    });
  }, [onPrintChecklist, project, meta, dataDate]);


  // Микротипографика ставится поверх всего отрисованного: и наших подписей,
  // и текста генератора, и строк, которые страница собрала сама. Одна точка
  // на документ — правило «не забыть обернуть» не переживает первой же новой
  // секции.
  const pageRef = useRef<HTMLDivElement>(null);
  useTypographDom(pageRef);

  return (
    <Page ref={pageRef}>
      <AppHeader onLogoClick={onLogoClick} />
      <Content gap={PAGE_GAP}>
        {/* Шапка документа набирается здесь, а не через `EntityHeader`:
            тот носит логотип и мелкий подзаголовок, и титул отчёта они
            превращали в строку реквизитов. */}
        <Stack gap='l'>
          <Stack gap='xs'>
            <Row gap='m' alignY='center' justify='between' wrap='wrap'>
              <Kicker>Управленческий отчёт</Kicker>
              <ChipMUI mode='badge' color='primary' variant='soft' size='xs'>
                7 минут чтения
              </ChipMUI>
            </Row>
            <ReportTitle as='h1'>Качество ведения рекламного кабинета</ReportTitle>
          </Stack>
          {/* Реквизиты — не сноска: по ним читатель проверяет, тот ли это
              кабинет и за какой срок, прежде чем поверить цифрам. */}
          <Row gap='s' wrap='wrap' alignY='center'>
            {[
              {label: `Проект «${project.name}»`},
              {
                label: AD_SYSTEM_LABEL[meta.adSystem] ?? meta.adSystem,
                icon: <AdSystemIcon size={ICON_PX} />,
              },
              {label: `Период ${reportPeriodTitle(meta.periodStart, meta.periodEnd)}`},
              {label: `Данные на ${longDate(dataDate)}`},
            ].map(fact => (
              <ChipMUI
                key={fact.label}
                mode='badge'
                color='neutral'
                variant='outline'
                size='s'
                leftIcon={fact.icon}
              >
                {fact.label}
              </ChipMUI>
            ))}
          </Row>

          {project.narrativeFailed && (
            <NarrativeNotice>
              Текстовая часть отчёта не сформирована: показываем только числа. Выводы, вопросы и
              задачи появятся в следующей сборке.
            </NarrativeNotice>
          )}
        </Stack>

        {/* Оглавление и документ стоят рядом: рейка липнет к своей
            колонке и следует за прокруткой, не отнимая у документа
            вертикали. На узком экране ряд схлопывается, и рейка сама
            ложится горизонтальной лентой — см. `AnchorNav`. */}
        <Row gap='xl' alignY='start' reflowAt='lg'>
          <AnchorNav anchors={anchors} activeId={activeId} />

          <ScorePartsContext.Provider value={project.scoreParts ?? null}>
          <Floors>
            {/* ── 1. Что происходит в рекламе ─────────────────────────── */}

            {project.summary && project.summary.length > 0 && (
              <Floor id={SECTION_IDS.summary}>
                <SummarySection
                  points={project.summary}
                  present={present}
                  onNavigate={handleNavigate}
                />
              </Floor>
            )}

            {/* `project.scoreZones &&` рядом с `present.score` — не вторая
            копия условия, а обязательное для TypeScript сужение типа:
            непустоту зон уже гарантирует сам признак, посчитанный в
            `sectionPresence` из того же поля выше. */}
            {present.score && project.scoreZones && (
              <Floor id={SECTION_IDS.score}>
                <ScoreSection
                  value={project.score ?? 0}
                  zones={project.scoreZones}
                  zone={project.scoreZone}
                  parts={project.scoreParts}
                  activityNote={teamUnmeasured ? ACTIVITY_FALLBACK_NOTE : null}
                />
              </Floor>
            )}

            {hasRisks && (
              <Floor id={SECTION_IDS.risks}>
                <RisksSection
                  risk={project.risk ?? 0}
                  dailySpend={project.riskDailySpend ?? 0}
                  pct={project.riskPct}
                  rows={risks}
                  rest={project.riskRest}
                  note={project.riskNote}
                  sensorWarning={project.sensorWarning}
                />
              </Floor>
            )}

            {/* `project.zeroResult &&` рядом с `hasZeroResult` — не вторая
            копия условия, а обязательное для TypeScript сужение типа:
            сам решает `hasZeroResult`, который вычислен из того же поля
            выше и уже гарантирует его непустоту. */}
            {hasZeroResult && project.zeroResult && (
              <Floor id={SECTION_IDS.zeroResult}>
                <ZeroResultSection data={project.zeroResult} totalSpend={project.spend ?? 0} />
              </Floor>
            )}

            {project.campaigns && project.campaigns.length > 0 && (
              <Floor
                id={SECTION_IDS.campaigns}
                badges={
                  <ChipMUI mode='badge' color='neutral' variant='outline' size='s'>
                    {pluralize(project.campaigns.length, CAMPAIGNS)}
                  </ChipMUI>
                }
              >
                <CampaignsSection campaigns={project.campaigns} />
              </Floor>
            )}

            {/* Плана по обращениям может не быть вовсе: у кабинета на другой
            метрике медиаплана плановой цены обращения не существует. */}
            {project.clients && (
              <Floor id={SECTION_IDS.clients}>
                <ClientsSection data={project.clients} onNavigate={handleNavigate} />
              </Floor>
            )}

            {/* Один `<Floor>` на два состояния этажа, а не по копии на
            каждую ветку: иначе `hideDescription` для заглушки пришлось бы
            гасить в двух местах вместо одного. */}
            {hasTeam && (
              <Floor id={SECTION_IDS.team} hideDescription={teamUnmeasured}>
                {/* `project.edits &&` рядом с `hasTeamCalendar` — не вторая
                копия условия, а сужение типа для TypeScript: непустоту
                календаря уже гарантирует сам флаг, вычисленный из того же
                поля выше. */}
                {hasTeamCalendar && project.edits ? (
                  <TeamSection edits={project.edits} />
                ) : (
                  <TeamUnmeasuredSection reason={project.editsStatus?.reason} />
                )}
              </Floor>
            )}

            {project.incidents && (
              <Floor id={SECTION_IDS.incidents}>
                <IncidentsSection data={project.incidents} />
              </Floor>
            )}

            {/* ── 2. Что с этим делать ────────────────────────────────── */}

            {project.questions && project.questions.length > 0 && (
              <Floor
                id={SECTION_IDS.questions}
                badges={
                  <ChipMUI mode='badge' color='primary' variant='soft' size='s'>
                    для планёрки
                  </ChipMUI>
                }
              >
                <QuestionsSection questions={project.questions} />
              </Floor>
            )}

            {project.tasks && project.tasks.length > 0 && (
              <Floor
                id={SECTION_IDS.tasks}
                badges={
                  <ChipMUI mode='badge' color='neutral' variant='outline' size='s'>
                    {pluralize(project.tasks.length, TASKS)}
                  </ChipMUI>
                }
              >
                <TasksSection tasks={project.tasks} onPrint={handlePrint} />
              </Floor>
            )}

            {/* ── 3. Контроль ─────────────────────────────────────────── */}

            {project.control && (
              <Floor
                id={SECTION_IDS.control}
                badges={
                  <NextCheckpointDate>
                    {longDate(project.control.nextReportDate)}
                  </NextCheckpointDate>
                }
              >
                <ControlSection
                  control={project.control}
                  dataDate={dataDate}
                  kpiName={project.kpi?.name}
                />
              </Floor>
            )}

            {/* ── 4. Чтобы не пропустить следующую проверку ───────────── */}

            {notifications && (
              <Floor id={SECTION_IDS.notifications}>
                <NotificationsSection notifications={notifications} />
              </Floor>
            )}
          </Floors>
          </ScorePartsContext.Provider>
        </Row>

        {/* Управление публичной ссылкой — инструмент владельца, а не часть
            рассказа: поэтому оно стоит после всех этажей, чипа в рейке не
            получает и гостю не показывается вовсе (тот приходит на страницу
            без этих пропов).

            Панель появляется только если хост дал способ ссылку создать:
            страница не берётся решать, есть ли у читателя такое право. */}
        {onCreatePublicLink && (
          <PublicLinkPanel
            link={publicLink}
            onCreate={onCreatePublicLink}
            onRevoke={onRevokePublicLink}
            busy={publicLinkBusy}
            error={publicLinkError}
          />
        )}

        <ReportFooter
          meta={meta}
          projectName={project.name}
          dataDate={dataDate}
          changeLogEvents={project.edits?.total}
          changeLogUsed={!teamUnmeasured}
        />
      </Content>
    </Page>
  );
}
